import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GridPaginationModel } from "@mui/x-data-grid";

import { RootState } from "@store/store";
import { ApplicationMarketplaceResume, ApplicationResume } from "@interfaces/application";
import {
    useLazyGetApplicationResumeQuery,
    useLazyGetApplicationsMarketplaceResumeQuery,
} from "@services/api/applications";
import { setFullSearching, setRowCountByCode } from "@store/slices/sessionSlice";
import { buildApplicationsRequest } from "../utils/buildApplicationsRequest";

/** Motivo por el que se vuelve a consultar la lista */
export type SolicitudesFetchReason = "change" | "fullSearch";

export interface UseSolicitudesResumeQueryOptions {
    /**
     * Se ejecuta antes de cada recarga automática (cambio de pestaña, filtros, orden, paginación,
     * compañías, financieras o `refreshKey`) y antes de cada búsqueda global.
     */
    onBeforeFetch?: (reason: SolicitudesFetchReason) => void;
    /** Al cambiar su valor se vuelve a consultar la lista */
    refreshKey?: unknown;
}

/**
 * Hook de consulta de las listas de Solicitudes (v1 `LoanAppDataGrid` y v2 `SolicitudesTable`/`ClientesTable`).
 * La construcción del request (estatus por pestaña, endpoint normal vs marketplace) vive en
 * `buildApplicationsRequest`, para que ambas versiones muestren siempre las mismas filas.
 */
export function useSolicitudesResumeQuery(listCode: string, options: UseSolicitudesResumeQueryOptions = {}) {
    const dispatch = useDispatch();

    const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [rows, setRows] = useState<Array<ApplicationResume | ApplicationMarketplaceResume>>([]);

    // Callback en ref para no volver a disparar los efectos cuando el componente lo recrea en cada render
    const onBeforeFetchRef = useRef(options.onBeforeFetch);
    onBeforeFetchRef.current = options.onBeforeFetch;

    // Identificador de la última recarga; las respuestas de recargas anteriores se descartan
    const lastRequestId = useRef(0);

    const fullSearch = useSelector((state: RootState) => state.session.filters.shared_filters.fullSearch);
    const companiesUser = useSelector((state: RootState) => state.session.filters.shared_filters.companies);
    const financialFilter = useSelector((state: RootState) => state.session.filters.shared_filters.financials);
    const configList = useSelector((state: RootState) =>
        state.session.filters.configList.find((t) => t.code === listCode),
    );

    const [triggerGetApplications, getApplicationsResponse] = useLazyGetApplicationResumeQuery();
    const [triggerGetAppsMarketplace, getAppsMarketplaceResponse] = useLazyGetApplicationsMarketplaceResumeQuery();

    const fetchPage = async (withPagination: boolean = true) => {
        try {
            const built = buildApplicationsRequest({
                listCode,
                withPagination,
                pagination,
                fullSearchText: fullSearch.text ?? "",
                filters: configList?.filters,
                sort: configList?.sort,
                companiesUser,
                financialFilter,
            });

            if (built === undefined) return undefined;

            return built.useMarketplace
                ? await triggerGetAppsMarketplace(built.urlParams.toString()).unwrap()
                : await triggerGetApplications(built.urlParams.toString()).unwrap();
        } catch (error) {
            console.error(error);
            return undefined;
        } finally {
            dispatch(setFullSearching(false));
        }
    };

    const refetch = async () => {
        const requestId = ++lastRequestId.current;
        const data = await fetchPage();

        if (requestId !== lastRequestId.current) return;

        if (data !== undefined) {
            dispatch(setRowCountByCode({ code: listCode, rowCount: data.total }));
            setRows(data.items);
        }
    };

    const reload = (reason: SolicitudesFetchReason) => {
        onBeforeFetchRef.current?.(reason);
        setRows([]);
        getApplicationsResponse.reset();
        getAppsMarketplaceResponse.reset();
        refetch();
    };

    useEffect(() => {
        if (listCode === "") return;

        reload("change");
    }, [
        listCode,
        configList?.filters,
        configList?.sort,
        pagination,
        companiesUser,
        financialFilter,
        options.refreshKey,
    ]);

    useEffect(() => {
        if (listCode === "") return;
        if (fullSearch.searching === false) return;

        reload("fullSearch");
    }, [fullSearch]);

    return {
        rows,
        setRows,
        rowCount: configList?.rowCount ?? 0,
        pagination,
        setPagination,
        isLoading: getApplicationsResponse.isFetching || getAppsMarketplaceResponse.isFetching,
        refetch,
        fetchPage,
    };
}
